/** State and handlers for the three-step welcome onboarding flow. */
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/useAuth';
import { useTier } from '@/features/tier/useTier';
import { persistUserDisplayName } from '@/services/authService';
import { setUserMarketingOptIn } from '@/services/userService';
import { MARKETING_OPT_IN_SOURCE_ONBOARDING } from '@/constants/marketingOptIn';
import {
  getDefaultDisplayNameFromAuthUser,
} from '@/features/onboarding/utils/onboardingDisplayNameUtils';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import { useTaskMutationsContext } from '@/features/tasks/context/useTaskMutationsContext';
import { parseTaskInput } from '@/features/tasks/utils/data/taskParser';
import { normalizeTagId } from '@/features/tasks/utils/ui/normalizeTaskTags';
import useAddSubtask from '@/features/tasks/hooks/mutations/use-task-add-subtask';
import { beginOnboardingWelcomePlanTour } from '@/features/onboarding/utils/onboardingWelcomePlanSession';
import {
  WELCOME_PROGRESS_TOTAL,
  getWelcomeProgressPercent,
  getWelcomeProgressStepNumber,
  resolveInitialWelcomeStep,
} from '@/features/onboarding/hooks/useOnboardingWelcomeSteps.utils';

export function useOnboardingWelcomeSteps() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profileDisplayName } = useTier();
  const { allTasks, tasksMap } = useTasksContext();
  const { updateTask } = useTaskMutationsContext();

  const [step, setStep] = useState(() =>
    resolveInitialWelcomeStep(user, profileDisplayName),
  );
  const [displayNameSaving, setDisplayNameSaving] = useState(false);
  const [displayNameErrorKey, setDisplayNameErrorKey] = useState(null);
  const displayNameSaveInFlight = useRef(false);
  const [parentTaskId, setParentTaskId] = useState(null);
  const [parentTitle, setParentTitle] = useState('');
  const [projectDone, setProjectDone] = useState(false);

  const [projectInput, setProjectInput] = useState('');
  const [subtaskInput, setSubtaskInput] = useState('');
  const [subtaskEditing, setSubtaskEditing] = useState(false);
  const [subtaskSaving, setSubtaskSaving] = useState(false);
  const [addedSubtask, setAddedSubtask] = useState(null);
  const projectInputRef = useRef(null);

  useLayoutEffect(() => {
    if (step !== 'project') {
      return undefined;
    }
    const frameId = requestAnimationFrame(() => {
      projectInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(frameId);
  }, [step]);

  const parentTask = parentTaskId ? tasksMap.get(parentTaskId) : undefined;
  const { handleSaveSubtask } = useAddSubtask(
    parentTask,
    allTasks,
    updateTask,
  );

  const stepNumber = getWelcomeProgressStepNumber(step);
  const parsedProject = useMemo(() => {
    const parsed = parseTaskInput(projectInput);
    if ((parsed.projects?.length ?? 0) > 0) {
      return parsed;
    }
    // Form path: bare project text (e.g. "werk") → normalizeTagId, same as TaskForm.
    const bareId = normalizeTagId(projectInput);
    if (!bareId) {
      return parsed;
    }
    return { ...parsed, projects: [bareId] };
  }, [projectInput]);

  const displayNameInitialValue = useMemo(
    () => getDefaultDisplayNameFromAuthUser(user),
    [user],
  );

  const handleDisplayNameSubmit = useCallback(
    async ({ displayName, marketingOptIn }) => {
      if (!user?.uid || displayNameSaveInFlight.current) {
        return;
      }
      displayNameSaveInFlight.current = true;
      setDisplayNameSaving(true);
      setDisplayNameErrorKey(null);
      try {
        await persistUserDisplayName(displayName);
        await setUserMarketingOptIn(user.uid, {
          optedIn: marketingOptIn,
          source: MARKETING_OPT_IN_SOURCE_ONBOARDING,
        });
        setStep('task');
      } catch {
        setDisplayNameErrorKey('onboarding.displayNameStep.errorSave');
      } finally {
        displayNameSaveInFlight.current = false;
        setDisplayNameSaving(false);
      }
    },
    [user?.uid],
  );

  const finishWelcomePartA = useCallback(() => {
    beginOnboardingWelcomePlanTour();
    navigate('/app/plan', { replace: true });
  }, [navigate]);

  const handleTaskAdded = useCallback((taskId, parsed) => {
    setParentTaskId(taskId);
    setParentTitle(parsed?.title?.trim() || '');
    setStep('project');
  }, []);

  const handleProjectSubmit = useCallback(async () => {
    const projects = parsedProject.projects ?? [];
    if (!projects.length || !parentTaskId || !user?.uid) {
      return;
    }
    await updateTask(parentTaskId, { projects }, user.uid);
    setProjectDone(true);
    setStep('subtask');
  }, [parsedProject.projects, parentTaskId, updateTask, user?.uid]);

  const saveSubtaskFromInput = useCallback(async () => {
    const trimmed = subtaskInput.trim();
    if (!trimmed || !parentTask || subtaskSaving || addedSubtask) {
      return null;
    }
    setSubtaskSaving(true);
    try {
      const subtaskId = await handleSaveSubtask({ title: trimmed });
      if (subtaskId) {
        setAddedSubtask({ id: subtaskId, title: trimmed });
        setSubtaskInput('');
        setSubtaskEditing(false);
        return subtaskId;
      }
      return null;
    } finally {
      setSubtaskSaving(false);
    }
  }, [
    subtaskInput,
    parentTask,
    subtaskSaving,
    addedSubtask,
    handleSaveSubtask,
  ]);

  const handleSubtaskEnter = useCallback(async () => {
    await saveSubtaskFromInput();
  }, [saveSubtaskFromInput]);

  const handleSubtaskPrimary = useCallback(async () => {
    if (addedSubtask) {
      finishWelcomePartA();
      return;
    }
    const trimmed = subtaskInput.trim();
    if (trimmed) {
      const subtaskId = await saveSubtaskFromInput();
      if (subtaskId) {
        finishWelcomePartA();
      }
      return;
    }
    if (parentTaskId) {
      finishWelcomePartA();
    }
  }, [
    addedSubtask,
    subtaskInput,
    saveSubtaskFromInput,
    parentTaskId,
    finishWelcomePartA,
  ]);

  const stepTotal = WELCOME_PROGRESS_TOTAL;
  const progressPercent = getWelcomeProgressPercent(stepNumber);

  return {
    step,
    stepNumber,
    stepTotal,
    progressPercent,
    displayNameInitialValue,
    displayNameSaving,
    displayNameErrorKey,
    handleDisplayNameSubmit,
    projectInputRef,
    projectInput,
    setProjectInput,
    parsedProject,
    projectDone,
    handleTaskAdded,
    handleProjectSubmit,
    parentTitle,
    parentTask,
    addedSubtask,
    subtaskEditing,
    subtaskSaving,
    subtaskInput,
    setSubtaskInput,
    startSubtaskEdit: () => setSubtaskEditing(true),
    handleSubtaskEnter,
    handleSubtaskPrimary,
  };
}
